"use client";
import React, { useState, useEffect } from "react";
import { FiExternalLink, FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import { adminApiService } from "services/api";

const ShopForm = ({ onClose, onAdd, onEdit, shop = null }) => {
  const [formData, setFormData] = useState({
    name: shop?.name || "",
    link: shop?.link || "",
    image: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Set initial preview for existing shop image
  useEffect(() => {
    if (shop?.image) {
      setPreviewUrl(
        `${process.env.REACT_APP_API_URL_Plain}/config/uploads/online_shop/${shop.image}`
      );
    }
  }, [shop]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      // Create preview URL for the new image
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);
    }
  };

  // Cleanup preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("link", formData.link);
      if (formData.image) {
        formDataToSend.append("online_shop_image", formData.image);
      }


      const response = shop
        ? await adminApiService.updateOnlineShop(shop.id, formDataToSend)
        : await adminApiService.addOnlineShop(formDataToSend);

      if (!response.ok)
        throw new Error(shop ? "Failed to update shop" : "Failed to add shop");

      const data = await response.json();
      if (shop) {
        onEdit(data);
      } else {
        onAdd(data);
      }
      onClose();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-black/60 fixed inset-0 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-2xl">
        <h2 className="mb-6 text-2xl font-bold text-gray-800">
          {shop ? "Edit Online Shop" : "Add New Online Shop"}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Shop Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full rounded-lg border border-gray-300 p-2.5 transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              required
            />
          </div>
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Shop URL
            </label>
            <input
              type="url"
              value={formData.link}
              onChange={(e) =>
                setFormData({ ...formData, link: e.target.value })
              }
              className="w-full rounded-lg border border-gray-300 p-2.5 transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              required
            />
          </div>
          <div className="mb-6">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Shop Logo
            </label>
            {previewUrl && (
              <div className="mb-4 flex justify-center">
                <div className="relative h-32 w-32">
                  <img
                    src={previewUrl}
                    alt="Shop logo preview"
                    className="h-full w-full rounded-lg object-contain shadow-md"
                  />
                  {shop && !formData.image && (
                    <div className="bg-black/50 absolute inset-0 flex items-center justify-center rounded-lg text-sm font-medium text-white">
                      Current Image
                    </div>
                  )}
                </div>
              </div>
            )}
            <input
              type="file"
              onChange={handleImageChange}
              className="w-full cursor-pointer rounded-lg border border-gray-300 p-2.5 file:mr-4 file:rounded-lg file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100"
              accept="image/*"
              required={!shop}
            />
            {shop && (
              <p className="mt-2 text-sm text-gray-500">
                Leave empty to keep current image
              </p>
            )}
          </div>
          {error && <p className="mb-4 text-sm text-red-500">{error}</p>}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2.5 text-gray-600 transition-colors hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-primary px-4 py-2.5 text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {loading
                ? shop
                  ? "Updating..."
                  : "Adding..."
                : shop
                ? "Update Shop"
                : "Add Shop"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const OnlineShops = () => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);

  const fetchShops = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await adminApiService.getOnlineShops();
      if (!response.ok) throw new Error("Failed to fetch shops");
      const data = await response.json();
      setShops(data);
    } catch (error) {
      console.error("Error fetching shops:", error);
      setError(error.message || "Failed to load online shops");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShops();
  }, []);

  const handleAddShop = (newShop) => {
    setShops([...shops, newShop]);
  };

  const handleEditShop = (updatedShop) => {
    setShops(
      shops.map((shop) => (shop.id === updatedShop.id ? updatedShop : shop))
    );
  };

  const handleDeleteShop = async (shopId) => {
    if (!window.confirm("Are you sure you want to delete this shop?")) return;

    try {
      const response = await adminApiService.deleteOnlineShop(shopId);

      if (!response.ok) throw new Error("Failed to delete shop");

      setShops(shops.filter((shop) => shop.id !== shopId));
    } catch (error) {
      console.error("Error deleting shop:", error);
      alert("Failed to delete shop. Please try again.");
    }
  };

  const openEditForm = (shop) => {
    setSelectedShop(shop);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedShop(null);
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md rounded-lg bg-white p-6 text-center shadow-md">
          <h2 className="mb-4 text-2xl font-bold text-red-600">
            Error Loading Shops
          </h2>
          <p className="mb-6 text-gray-600">{error}</p>
          <button
            onClick={fetchShops}
            className="rounded bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 px-4 py-16 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-20 text-center">
          <h1 className="mb-4 text-4xl font-bold text-gray-900 md:text-5xl">
            Our
            <span className="text-transparent bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text">
              Online
            </span>
            Shops
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-gray-600">
            Discover our products on these trusted e-commerce platforms
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-6 inline-flex items-center rounded-lg bg-primary px-5 py-2.5 text-white transition-all hover:bg-blue-700 hover:shadow-lg"
          >
            <FiPlus className="mr-2" />
            Add New Shop
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
          </div>
        ) : shops.length === 0 ? (
          <div className="rounded-xl bg-white p-12 text-center shadow-lg">
            <h3 className="text-xl text-gray-500">
              No online shops available at the moment
            </h3>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {shops.map((shop, index) => (
              <div
                key={index}
                className="group overflow-hidden rounded-xl bg-white shadow-md transition-all duration-300 hover:-translate-y-2 hover:shadow-xl"
              >
                <div className="flex flex-col items-center p-8">
                  <div className="mb-8 flex h-24 w-full items-center justify-center">
                    <img
                      src={`${process.env.REACT_APP_API_URL_Plain}/config/uploads/online_shop/${shop.image}`}
                      alt={`${shop.name} logo`}
                      className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>
                  <h2 className="mb-6 text-center text-xl font-semibold text-gray-800">
                    {shop.name}
                  </h2>
                  <div className="flex w-full flex-col gap-3">
                    <a
                      href={shop.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-full items-center justify-center rounded-lg bg-primary px-5 py-3 text-white transition-all duration-300 hover:bg-blue-700 hover:shadow-md"
                    >
                      Visit Store
                      <FiExternalLink className="ml-2" size={16} />
                    </a>
                    <div className="flex gap-3">
                      <button
                        onClick={() => openEditForm(shop)}
                        className="flex-1 rounded-lg border border-gray-300 px-4 py-3 text-gray-700 transition-all hover:bg-gray-50 hover:shadow-sm"
                      >
                        <FiEdit2 className="mx-auto" size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteShop(shop.id)}
                        className="flex-1 rounded-lg border border-red-300 px-4 py-3 text-red-600 transition-all hover:bg-red-50 hover:shadow-sm"
                      >
                        <FiTrash2 className="mx-auto" size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {showForm && (
        <ShopForm
          onClose={closeForm}
          onAdd={handleAddShop}
          onEdit={handleEditShop}
          shop={selectedShop}
        />
      )}
    </div>
  );
};

export default OnlineShops;
